import React, { useState, useEffect, useCallback } from 'react';
import '../styles/components/BudgetVarianceTracker.css';
import Icon from './Icon';
import EmptyState from './EmptyState';
import LoadErrorBox from './ui/LoadErrorBox';
import { MetricCard } from './MetricCards';
import { Modal } from './ui/Modal';
import { useNotifications } from './NotificationProvider';
import { useSettings } from '../contexts/SettingsContext';
import { useRole } from '../hooks/useRole';
import { errorMessageOf } from '../utils/apiErrors';
import { getBudgetCurrency } from '../utils/budgetStorage';
import {
  loadVariances,
  createVariance,
  approveVariance,
  rejectVariance,
  type BudgetVariance,
} from '../services/BudgetVarianceService';

type Filter = 'all' | 'needs-approval' | 'decided';

const varianceClass = (amount: number) => (amount > 0 ? 'variance-over' : amount < 0 ? 'variance-under' : 'variance-neutral');
const signed = (n: number) => (n > 0 ? '+' : '');

const BudgetVarianceTracker: React.FC = () => {
  const { showError, showSuccess } = useNotifications();
  const { formatCurrency } = useSettings();
  const { canEdit } = useRole();
  const [variances, setVariances] = useState<BudgetVariance[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [decision, setDecision] = useState<{ id: string; notes: string } | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ vendor_name: '', original_quote_amount: '', actual_invoice_amount: '', reason: '' });
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setVariances(await loadVariances());
    } catch (error) {
      setLoadError(error ?? new Error('load failed'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // The Add Variance button sits in the page header.
  useEffect(() => {
    const open = () => { if (canEdit) setAdding(true); };
    window.addEventListener('budget-variance:add', open);
    return () => window.removeEventListener('budget-variance:add', open);
  }, [canEdit]);

  const needsApproval = variances.filter(v => v.requires_approval && (v.status === 'flagged' || v.status === 'acknowledged'));
  const decided = variances.filter(v => v.status === 'approved' || v.status === 'rejected');
  const overrun = variances.filter(v => v.variance_amount > 0).reduce((sum, v) => sum + v.variance_amount, 0);
  const currency = variances.find(v => v.currency)?.currency ?? getBudgetCurrency() ?? undefined;

  const shown = variances.filter(v => (
    filter === 'needs-approval' ? needsApproval.includes(v) : filter === 'decided' ? decided.includes(v) : true
  ));

  const decide = async (approved: boolean) => {
    if (!decision || saving) return;
    setSaving(true);
    try {
      await (approved ? approveVariance : rejectVariance)(decision.id, decision.notes);
      setDecision(null);
      await load();
    } catch (error) {
      showError(errorMessageOf(error, 'Failed to update the variance. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const submitNew = async () => {
    if (saving) return;
    setFormError(null);
    setSaving(true);
    try {
      await createVariance(draft);
      setAdding(false);
      setDraft({ vendor_name: '', original_quote_amount: '', actual_invoice_amount: '', reason: '' });
      showSuccess('Variance recorded');
      await load();
    } catch (error) {
      setFormError(errorMessageOf(error, 'Could not save the variance.'));
    } finally {
      setSaving(false);
    }
  };

  if (loadError) return <div className="wt-container fade-in"><LoadErrorBox what="budget variances" error={loadError} onRetry={load} retrying={loading} /></div>;

  return (
    <div className="wt-container fade-in">
      <section className="stats-grid">
        <MetricCard label="All variances" value={variances.length} tone="pink" icon="alert" active={filter === 'all'} onClick={() => setFilter('all')} />
        <MetricCard label="Needs approval" value={needsApproval.length} tone="blue" icon="flag" active={filter === 'needs-approval'} onClick={() => setFilter('needs-approval')} />
        <MetricCard label="Decided" value={decided.length} tone="green" icon="check" active={filter === 'decided'} onClick={() => setFilter('decided')} />
        <MetricCard label="Over quote" value={formatCurrency(overrun, currency)} tone={overrun > 0 ? 'red' : 'green'} icon="chart" />
      </section>

      {!loading && shown.length === 0 ? (
        <EmptyState
          icon="check"
          title={filter === 'all' ? 'No budget variances' : 'Nothing to show for this filter'}
          description={filter === 'all' ? 'Every vendor invoice is tracking to its quote.' : undefined}
          action={canEdit && filter === 'all' ? <button className="primary-btn" onClick={() => setAdding(true)}>Record a variance</button> : undefined}
        />
      ) : (
        <div className="bvt-list">
          {shown.map((variance) => (
            <div key={variance.id} className={`bvt-card bvt-${variance.status}`}>
              <div className="bvt-header">
                <div className="bvt-vendor-info">
                  <h4 className="bvt-vendor-name">{variance.vendor_name}</h4>
                  <p className="bvt-vendor-service">{variance.reason || 'No reason given'}</p>
                </div>
                <div className={`variance-badge ${varianceClass(variance.variance_amount)}`}>
                  {signed(variance.variance_amount)}{formatCurrency(variance.variance_amount, variance.currency ?? undefined)}
                </div>
              </div>
              <div className="bvt-amounts">
                <div className="amount-item">
                  <span className="label">Quote</span>
                  <span className="value">{formatCurrency(variance.original_quote_amount, variance.currency ?? undefined)}</span>
                </div>
                <div className="amount-item">
                  <span className="label">Invoice</span>
                  <span className="value">{formatCurrency(variance.actual_invoice_amount, variance.currency ?? undefined)}</span>
                </div>
                <div className={`amount-item variance ${varianceClass(variance.variance_amount)}`}>
                  <span className="label">Variance</span>
                  <span className="value">{signed(variance.variance_percentage)}{variance.variance_percentage.toFixed(1)}%</span>
                </div>
              </div>
              {variance.requires_approval && (variance.status === 'flagged' || variance.status === 'acknowledged') && (
                <div className="bvt-alert">
                  <Icon name="alert" size={16} />
                  <span>Needs approval (5% or more off the quote)</span>
                </div>
              )}
              {(variance.status === 'approved' || variance.status === 'rejected') && (
                <div className="bvt-approval-info">
                  <p className="approval-label">{variance.status === 'approved' ? 'Approved' : 'Rejected'}{variance.approved_by ? ` by ${variance.approved_by}` : ''}</p>
                  {variance.approval_notes && <p className="approval-notes">{variance.approval_notes}</p>}
                </div>
              )}
              {canEdit && (variance.status === 'flagged' || variance.status === 'acknowledged') && (
                <div className="bvt-actions">
                  <button className="btn-approve" onClick={() => setDecision({ id: variance.id, notes: '' })}>
                    <Icon name="check" size={14} /> Decide
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal
        open={!!decision}
        onClose={() => setDecision(null)}
        title="Approve or reject this variance?"
        size="small"
        footer={
          <div className="modal-actions">
            <button className="btn-approve" disabled={saving} onClick={() => decide(true)}><Icon name="check" size={14} /> Approve</button>
            <button className="btn-reject" disabled={saving} onClick={() => decide(false)}><Icon name="x" size={14} /> Reject</button>
            <button className="btn-cancel" onClick={() => setDecision(null)}>Cancel</button>
          </div>
        }
      >
        {decision && (
          <>
            <p className="modal-note">Your name is recorded with the decision. Add a note about why.</p>
            <textarea
              aria-label="Notes"
              rows={3}
              value={decision.notes}
              onChange={(e) => setDecision({ ...decision, notes: e.target.value })}
            />
          </>
        )}
      </Modal>

      <Modal
        open={adding}
        onClose={() => setAdding(false)}
        title="Record a variance"
        size="small"
        footer={
          <div className="modal-actions">
            <button className="btn-approve" disabled={saving} onClick={submitNew}>Save</button>
            <button className="btn-cancel" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        }
      >
        <div className="bvt-form-grid">
          <div className="form-group">
            <label htmlFor="bv-vendor">Vendor</label>
            <input id="bv-vendor" value={draft.vendor_name} onChange={(e) => setDraft({ ...draft, vendor_name: e.target.value })} />
          </div>
          <div className="form-group">
            <label htmlFor="bv-quote">Quote{currency ? ` (${currency})` : ''}</label>
            <input id="bv-quote" inputMode="decimal" value={draft.original_quote_amount} onChange={(e) => setDraft({ ...draft, original_quote_amount: e.target.value })} />
          </div>
          <div className="form-group">
            <label htmlFor="bv-invoice">Invoice{currency ? ` (${currency})` : ''}</label>
            <input id="bv-invoice" inputMode="decimal" value={draft.actual_invoice_amount} onChange={(e) => setDraft({ ...draft, actual_invoice_amount: e.target.value })} />
          </div>
          <div className="form-group">
            <label htmlFor="bv-reason">Reason</label>
            <textarea id="bv-reason" rows={2} value={draft.reason} onChange={(e) => setDraft({ ...draft, reason: e.target.value })} />
          </div>
          {formError && <p role="alert" className="modal-note">{formError}</p>}
        </div>
      </Modal>
    </div>
  );
};

export default BudgetVarianceTracker;
